/*
 * dexDetailScanGate.ts
 *
 * Decides WHEN a live-scan frame should be probed as a Pokémon GO **species
 * detail page**, and whether a successful read should be written.
 *
 * ── Why a stillness gate ────────────────────────────────────────────────────
 * The Pokédex GRID scanner reads a moving target: the user scrolls, and the
 * scanner has to pick usable frames out of that motion (which is where the
 * whole smear / settle / foreground-floor machinery comes from). A DETAIL page
 * is the opposite — the user opens one species and it sits there. So instead of
 * fighting motion we simply refuse to look at it: N consecutive stable frames
 * must pass before we probe at all.
 *
 * That single rule buys two things at once:
 *
 *  1. CORRECTNESS. Any frame captured while the page is still settling (the user
 *     scrolling down to bring the lower variant cards into view) is never read,
 *     so a half-scrolled card can't be misclassified. This is belt-and-braces
 *     rather than load-bearing — `extractPokemonDetailPage` already OMITS cards
 *     it could not see rather than asserting them missing, so a mid-scroll frame's
 *     worst case is "fewer cards", never "wrong cards".
 *
 *  2. COST. `extractPokemonDetailPage` must run a Vision OCR pass BEFORE it can
 *     tell whether the screen is even a detail page — there is no cheap probe.
 *     Running that on every frame would double the per-second Vision work
 *     alongside the grid extractor. Gating on stillness means we probe ONCE PER
 *     DWELL instead of once per frame: a scrolling user costs nothing, and a user
 *     parked on a species page pays a single probe.
 *
 * The gate is deliberately pure — no frames, no OCR, no store — so the policy is
 * unit-testable without a DOM.
 */

/**
 * Consecutive stable frames required before the first probe of a dwell.
 *
 * THIS IS THE WHOLE SWITCH LATENCY. The broadcast extension writes frames at a
 * fixed 1 Hz (`writeInterval` in SampleHandler.swift), so this constant is the
 * dwell in SECONDS before the HUD even looks at a species page — and every swipe
 * to the next species resets it.
 *
 * Lowered 5 -> 2 on 2026-07-21 after device screenshots showed the HUD still
 * reading BULBASAUR #1 across Ivysaur and Venusaur: a user flicking through
 * species faster than five seconds each never saw the HUD catch up. 5 was a
 * guess made before anyone had watched the feature on device; a detail page the
 * user has stopped on is, in the owner's words, "definitely very still".
 *
 * Both reasons for the old value had weakened:
 *  - CORRECTNESS was already "belt-and-braces rather than load-bearing" (see the
 *    header) — `extractPokemonDetailPage` OMITS cards it could not see rather
 *    than asserting them missing, and the scroll-stability gate has already
 *    rejected every moving frame before one gets here, so the marginal frames
 *    were confirming a page that is by construction not moving.
 *  - COST: a probe used to mean re-encoding the frame. Since the 2026-07-21
 *    encode memo it shares the already-encoded frame with the grid path, and the
 *    detail extractor is a SINGLE `recognizeRegions` call for the whole page.
 *
 * 2 keeps one frame of margin over "probe the instant motion stops" while
 * cutting ~3s off every species switch. Do not raise it without re-reading the
 * above: this is felt latency, not safety margin.
 */
export const DEFAULT_STILL_FRAMES_REQUIRED = 2;

export interface DetailScanGateConfig {
  /**
   * How many consecutive stable frames must pass before probing. At the live
   * scanner's ~1 Hz capture this is also the dwell time in seconds, so it is the
   * whole latency budget for the feature — raising it makes the feature feel
   * slower, not just safer.
   */
  stillFramesRequired?: number;
}

export interface DetailScanGate {
  /**
   * Feed one frame's stability verdict (from `decideFrameStability`). Returns
   * true when THIS frame should be probed as a detail page.
   *
   * Exactly one probe is authorised per dwell: once a dwell has probed, further
   * stable frames return false until motion resets the gate. Any unstable frame
   * ends the dwell, so a user who scrolls, stops, scrolls and stops again gets
   * one probe per stop — which is precisely the "scroll down to reveal the lower
   * cards" flow, re-probed after each move.
   */
  noteFrame(stable: boolean): boolean;
  /**
   * Count a frame toward the dwell as STILL, WITHOUT authorising a probe.
   *
   * For frames the pipeline drops before they reach `noteFrame` — specifically
   * the byte-identical ones the frame dedup suppresses on a held-steady screen.
   * Those frames are the strongest possible evidence the page is not moving
   * (identical bytes), which is exactly what the dwell is trying to establish,
   * yet the dedup discards them because the GRID reader gains nothing from a
   * repeat.
   *
   * Without this the dwell counted only ADMITTED frames, and the dedup admits
   * roughly one static frame in three (DUPLICATE_RECENT_REINGEST_INTERVAL), so a
   * 2-frame dwell took ~6s of wall clock on a page the user was holding
   * perfectly still. Counting held frames here means the dwell is satisfied in
   * real time and the next admitted frame probes immediately.
   *
   * Deliberately does NOT return a probe authorisation: the caller is on a path
   * that has already decided not to run the pipeline for this frame, and
   * consuming the dwell's single probe there would waste it.
   */
  noteHeldFrame(): void;
  /**
   * Report what the probe this dwell authorised actually found, so a WASTED probe
   * can be retried without the user having to move the page.
   *
   * Pass `true` only when the frame WAS a species detail page but could not be
   * turned into a usable read (page still cross-fading after a swipe, title band
   * caught mid-animation). That re-arms the dwell so the next stable frame probes
   * again. Without this, one unlucky probe strands the HUD on the PREVIOUS
   * species for as long as the user sits still — which is indistinguishable from
   * the feature being broken.
   *
   * Pass `false` for an ordinary grid frame (`isDetailPage === false`). That is
   * not a failure, it is the answer, and re-arming there would put the probe back
   * on every frame of a normal grid scan — exactly the cost the dwell exists to
   * avoid.
   *
   * Retries are capped per dwell so a page that simply cannot be read (an unknown
   * form, a heavy costume overlay) degrades to the old once-per-dwell behaviour
   * instead of probing forever.
   */
  noteProbeOutcome(retryable: boolean, foundDetailPage?: boolean): void;
  /**
   * Report that the frame's CONTENT changed while the page stayed still — i.e.
   * the dedup admitted a frame whose hash differs from the last one it read.
   *
   * This is the swipe from one species page to the next, and it is invisible to
   * the stability gate that `noteFrame` speaks for. That gate compares luma
   * bands, and two species detail pages are the SAME page with a different
   * sprite and title: the 2026-07-23 device log measured band diffs of 0.3–4.0
   * across five species switches against a threshold of 8, so every frame read
   * "stable", the dwell never ended, and `probedThisDwell` stayed latched from
   * the first species. 122 stable frames spanning #18 → #14 produced exactly ONE
   * probe, and the HUD sat on the species the user had left (PIDGEOT #18 over a
   * screen showing PIDGEY #16).
   *
   * So the dwell has to end on either signal: the page MOVED (`noteFrame(false)`)
   * or the page CHANGED underneath us. Cost is bounded two ways — a changed view
   * must go still again before it probes (`stillFrames` resets), and the re-arm
   * only applies when the last probe actually found a species page, so a grid
   * scan (whose content changes on every scroll) never buys extra probes.
   */
  noteViewChanged(): void;
  /**
   * Record that a species+form was successfully written, so a subsequent dwell on
   * the SAME page doesn't rewrite it. Keyed per session.
   */
  noteApplied(dexNumber: number, formId: string | null): void;
  /** True when this species+form has already been written this session. */
  hasApplied(dexNumber: number, formId: string | null): boolean;
  /** Consecutive stable frames observed — diagnostics/tests. */
  readonly stillFrames: number;
  /** Drop all state (new session / variant switch). */
  reset(): void;
}

function appliedKey(dexNumber: number, formId: string | null): string {
  return `${dexNumber}|${formId ?? ""}`;
}

/** Extra probes a single dwell may spend retrying an unreadable detail page. */
export const MAX_RETRIES_PER_DWELL = 2;

export function createDetailScanGate(
  config: DetailScanGateConfig = {},
): DetailScanGate {
  const required = Math.max(1, config.stillFramesRequired ?? DEFAULT_STILL_FRAMES_REQUIRED);
  let stillFrames = 0;
  let probedThisDwell = false;
  let retriesThisDwell = 0;
  // Did the last probe of this session land on a species page? Gates the
  // content-change re-arm to the screen the feature actually lives on.
  let lastProbeFoundDetail = false;
  const applied = new Set<string>();

  return {
    noteFrame(stable: boolean): boolean {
      if (!stable) {
        // Motion ends the dwell. The next run of stable frames is a NEW look at
        // the page (the user may have scrolled fresh cards into view), so it
        // earns its own probe.
        stillFrames = 0;
        probedThisDwell = false;
        retriesThisDwell = 0;
        return false;
      }
      stillFrames += 1;
      if (stillFrames < required || probedThisDwell) return false;
      probedThisDwell = true;
      return true;
    },
    noteHeldFrame(): void {
      stillFrames += 1;
    },
    noteViewChanged(): void {
      // Only a dwell that has already probed can be stranded, and only on a
      // species page is a re-probe worth a Vision pass.
      if (!probedThisDwell || !lastProbeFoundDetail) return;
      probedThisDwell = false;
      retriesThisDwell = 0;
      // Unlike the retry re-arm, this DOES reset the dwell: the pixels are new,
      // so "already known still" no longer holds and the page may be mid-swipe.
      stillFrames = 0;
    },
    noteProbeOutcome(retryable: boolean, foundDetailPage = retryable): void {
      lastProbeFoundDetail = foundDetailPage;
      if (!retryable) return;
      if (retriesThisDwell >= MAX_RETRIES_PER_DWELL) return;
      retriesThisDwell += 1;
      // Re-arm WITHOUT resetting stillFrames: the page is already known still, so
      // the retry lands on the very next frame rather than serving the dwell again.
      probedThisDwell = false;
    },
    noteApplied(dexNumber: number, formId: string | null): void {
      applied.add(appliedKey(dexNumber, formId));
    },
    hasApplied(dexNumber: number, formId: string | null): boolean {
      return applied.has(appliedKey(dexNumber, formId));
    },
    get stillFrames(): number {
      return stillFrames;
    },
    reset(): void {
      stillFrames = 0;
      probedThisDwell = false;
      retriesThisDwell = 0;
      lastProbeFoundDetail = false;
      applied.clear();
    },
  };
}
